import { useState, useEffect } from 'react';
import { useUpdateExtension } from '../../lib/api/hooks.js';
import { Icon } from '../../components/ui/Icon.js';
import { Section, Field, Toggle, FormActions } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Follow-me: ring somewhere else when the seat does not answer.
 *
 * Ordered, because the order is the feature — each step rings only after the
 * one before it gave up. That is also why every step carries its own ring time
 * rather than sharing one: the desk beside you is worth ten seconds, a
 * colleague upstairs perhaps twenty, and a mobile long enough to be dug out of
 * a pocket.
 */

const MAX_STEPS = 5;

interface Step { destination: string; ring_sec: number }

export function FollowMeEditor({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const update = useUpdateExtension(tenantId);

  const [steps, setSteps] = useState<Step[]>([]);
  const [afterSec, setAfterSec] = useState('');
  const [keepOriginalCid, setKeepOriginalCid] = useState(true);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const e = ext as unknown as {
      follow_me?: Step[] | null;
      follow_me_after_sec?: number | null;
      follow_me_caller_id?: string;
    };
    setSteps(e.follow_me ?? []);
    setAfterSec(e.follow_me_after_sec ? String(e.follow_me_after_sec) : '');
    setKeepOriginalCid((e.follow_me_caller_id ?? 'original') === 'original');
    setDirty(false);
  }, [ext]);

  const touch = <T,>(fn: () => T): T => { setDirty(true); return fn(); };

  const setStep = (i: number, patch: Partial<Step>) => touch(() =>
    setSteps((s) => s.map((st, n) => (n === i ? { ...st, ...patch } : st))));

  const move = (i: number, by: number) => touch(() => setSteps((s) => {
    const to = i + by;
    if (to < 0 || to >= s.length) return s;
    const copy = [...s];
    [copy[i], copy[to]] = [copy[to]!, copy[i]!];
    return copy;
  }));

  const err = update.error instanceof ApiClientError ? update.error : null;

  const submit = () => {
    const clean = steps
      .map((s) => ({ destination: s.destination.trim(), ring_sec: s.ring_sec }))
      .filter((s) => s.destination.length >= 2);
    update.mutate({
      id: ext.id,
      follow_me: clean.length > 0 ? clean : null,
      follow_me_after_sec: afterSec ? Number(afterSec) : null,
      follow_me_caller_id: keepOriginalCid ? 'original' : 'extension',
    }, { onSuccess: () => setDirty(false) });
  };

  /** External steps are accepted and stored, but cannot ring until a trunk exists. */
  const external = steps.filter((s) => s.destination.trim() && !/^\d{2,6}$/.test(s.destination.trim()));

  return (
    <>
      {err && Object.keys(err.fieldErrors).length === 0 && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <Section title="Follow me"
        description="Ring these in order when the seat does not answer. Voicemail still catches the call if nobody does.">
        <Field label="Ring this seat first for" htmlFor="fm-after"
          hint="Seconds before follow-me starts. Blank uses the seat's normal ring time.">
          <input id="fm-after" className="arc-input" inputMode="numeric"
            value={afterSec} placeholder={String(ext.call_timeout_sec ?? 20)}
            onChange={(e) => touch(() => setAfterSec(e.target.value.replace(/\D/g, '')))} />
        </Field>
      </Section>

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">Steps</h2>
          <span className="arc-subtle">{steps.length} of {MAX_STEPS}</span>
        </div>
        <div className="arc-card-body">
          {steps.length === 0 && (
            <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
              No steps yet. Calls ring the seat and then go to voicemail.
            </p>
          )}

          {steps.map((s, i) => (
            <div key={i} className="arc-row"
              style={{ gap: 8, marginBottom: 8, alignItems: 'flex-end' }}>
              <span className="arc-subtle arc-mono"
                style={{ width: 18, textAlign: 'right', paddingBottom: 8 }}>{i + 1}</span>

              <div className="arc-field" style={{ margin: 0, flex: 1 }}>
                {i === 0 && <label className="arc-label">Ring</label>}
                <input className="arc-input arc-mono" value={s.destination}
                  placeholder="extension number"
                  onChange={(e) => setStep(i, { destination: e.target.value })} />
              </div>

              <div className="arc-field" style={{ margin: 0, width: 120 }}>
                {i === 0 && <label className="arc-label">For</label>}
                <input className="arc-input" inputMode="numeric" value={String(s.ring_sec)}
                  onChange={(e) => setStep(i, {
                    ring_sec: Math.max(5, Math.min(120, Number(e.target.value.replace(/\D/g, '')) || 20)),
                  })} />
              </div>

              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                title="Move up" aria-label={`Move step ${i + 1} up`}
                disabled={i === 0} onClick={() => move(i, -1)}>
                <Icon name="chevronRight" size={14} />
              </button>
              <button type="button"
                className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-danger-ghost"
                title="Remove this step" aria-label={`Remove step ${i + 1}`}
                onClick={() => touch(() => setSteps((v) => v.filter((_, n) => n !== i)))}>
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}

          {steps.length < MAX_STEPS && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              style={{ marginTop: 4 }}
              onClick={() => touch(() => setSteps((v) => [...v, { destination: '', ring_sec: 20 }]))}>
              <Icon name="plus" size={13} /> Add a step
            </button>
          )}

          {/*
            External steps are stored but cannot ring yet.
            "Ring my mobile" is the commonest reason to want this and it needs an
            outbound route and a carrier. The step is kept so the configuration
            is ready, and the switch skips it rather than sending the caller into
            a failure — but saying nothing here would leave somebody waiting for
            a phone that is never going to ring.
          */}
          {external.length > 0 && (
            <div className="arc-banner arc-banner-warn" style={{ marginTop: 12 }}>
              <Icon name="warning" size={14} />
              <span>
                {external.length === 1 ? 'One step is' : `${external.length} steps are`} an
                outside number. These are saved, but will not ring until a carrier
                trunk is connected — internal extensions in the list still work.
              </span>
            </div>
          )}
        </div>
      </div>

      <Section title="Caller ID" columns={1}>
        <Toggle id="fm-cid" label="Show the original caller's number"
          hint={keepOriginalCid
            ? 'The person answering sees who actually called.'
            : `Followed calls will show ${ext.number} instead, so it is clear the call came via this seat. Required for outside numbers once a carrier is connected — most will reject a caller ID you do not own.`}
          checked={keepOriginalCid} onChange={(v) => touch(() => setKeepOriginalCid(v))} />
      </Section>

      <FormActions
        dirty={dirty} saving={update.isPending}
        error={err && Object.keys(err.fieldErrors).length === 0 ? err.message : null}
        onReset={() => { setDirty(false); }}
        onSave={submit}
      />
    </>
  );
}
